import { Bell } from 'lucide-react';
import { EXPENSE_CATEGORIES, DEFAULT_BUDGETS } from '../../utils/expenseHelpers';

/**
 * Monthly Budget modal — set per-category spending caps and visualize
 * progress toward each. Pure presentation; state lives in the parent.
 *
 * Extracted from Expenses.js as part of the giant-component refactor
 * (Feb 2026). Behavior is byte-for-byte equivalent.
 */
const BudgetModal = ({
  open,
  expenses,
  budgets,
  onSaveBudgets,
  onClose,
}) => {
  if (!open) return null;
  const currentMonth = new Date().toISOString().slice(0, 7);

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0, 0, 0, 0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
        padding: '1rem',
      }}
    >
      <div
        style={{
          background: '#fff',
          borderRadius: '16px',
          padding: '2rem',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <Bell size={24} color="#059669" />
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>Monthly Budget Limits</h2>
        </div>

        <p style={{ color: '#6b7280', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
          Set spending limits per category. You'll receive alerts when approaching or exceeding these limits.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {EXPENSE_CATEGORIES.map((category) => {
            const spent = expenses
              .filter((e) => e.category === category && e.date?.startsWith(currentMonth))
              .reduce((sum, e) => sum + (e.amount || 0), 0);
            const budget = budgets[category] || DEFAULT_BUDGETS[category];
            const percentage = Math.min((spent / budget) * 100, 100);

            return (
              <div
                key={category}
                data-testid={`budget-row-${category}`}
                style={{ padding: '1rem', background: '#f9fafb', borderRadius: '10px', border: '1px solid #e5e7eb' }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <span style={{ fontWeight: 500 }}>{category}</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <span style={{ fontSize: '0.85rem', color: '#6b7280' }}>$</span>
                    <input
                      type="number"
                      value={budgets[category] || DEFAULT_BUDGETS[category]}
                      onChange={(e) => onSaveBudgets({ ...budgets, [category]: parseFloat(e.target.value) || 0 })}
                      style={{
                        width: '80px',
                        padding: '0.375rem 0.5rem',
                        border: '1px solid #e5e7eb',
                        borderRadius: '6px',
                        fontSize: '0.875rem',
                        textAlign: 'right',
                      }}
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{ flex: 1, height: '8px', background: '#e5e7eb', borderRadius: '4px', overflow: 'hidden' }}>
                    <div
                      style={{
                        width: `${percentage}%`,
                        height: '100%',
                        background: percentage >= 100 ? '#dc2626' : percentage >= 80 ? '#f59e0b' : '#10b981',
                        transition: 'width 0.3s',
                      }}
                    />
                  </div>
                  <span
                    style={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: percentage >= 100 ? '#dc2626' : percentage >= 80 ? '#f59e0b' : '#10b981',
                    }}
                  >
                    ${spent.toFixed(0)} / ${budget}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        <button
          onClick={onClose}
          data-testid="save-budget-button"
          style={{
            marginTop: '1.5rem',
            width: '100%',
            padding: '0.75rem',
            background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
            color: '#fff',
            border: 'none',
            borderRadius: '8px',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Save & Close
        </button>
      </div>
    </div>
  );
};

export default BudgetModal;
